/**
 * Kioraa approximate local-currency display.
 *
 * WHAT THIS IS: the store charges in USD and cannot do otherwise — Shopify only
 * supports local presentment currencies through Shopify Payments or Adyen, and
 * neither is available to this store. This shows the shopper an APPROXIMATE
 * amount in their own currency next to the USD price, at live market rates, and
 * says plainly that the charge is in USD.
 *
 * WHAT THIS IS NOT: it does not change the charged currency. Never present the
 * converted figure as the price. The "≈" and the "charged in USD" label are not
 * decoration — they are what keeps this honest and chargeback-safe.
 *
 * Rates: frankfurter.app (European Central Bank reference rates), with
 * open.er-api.com as a fallback. Cached in localStorage for 12 hours.
 * The customer's own bank sets the final rate and may add a foreign
 * transaction fee, so the real charge will differ slightly. The copy says so.
 */
(function () {
  'use strict';

  var COUNTRY_TO_CURRENCY = {
    AU: 'AUD',
    CA: 'CAD',
    DE: 'EUR',
    GB: 'GBP',
    IN: 'INR',
    NZ: 'NZD',
  };

  var SYMBOLS = {
    AUD: 'A$',
    CAD: 'C$',
    EUR: '€',
    GBP: '£',
    INR: '₹',
    NZD: 'NZ$',
  };

  var CACHE_KEY = 'kioraa_fx_v1';
  var CACHE_MS = 12 * 60 * 60 * 1000;
  var MARKED = 'data-kioraa-approx';

  function targetCurrency() {
    var country =
      (window.Shopify && window.Shopify.country) ||
      document.documentElement.getAttribute('data-country') ||
      'US';
    return COUNTRY_TO_CURRENCY[country.toUpperCase()] || null;
  }

  function readCache(currency) {
    try {
      var raw = window.localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var c = JSON.parse(raw);
      if (!c || !c.at || Date.now() - c.at > CACHE_MS) return null;
      return c.rates && typeof c.rates[currency] === 'number' ? c.rates[currency] : null;
    } catch (e) {
      return null;
    }
  }

  function writeCache(rates) {
    try {
      window.localStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), rates: rates }));
    } catch (e) {
      /* private mode — just skip caching */
    }
  }

  function fetchRates() {
    var wanted = 'AUD,CAD,EUR,GBP,INR,NZD';
    return fetch('https://api.frankfurter.app/latest?from=USD&to=' + wanted)
      .then(function (r) {
        if (!r.ok) throw new Error('primary ' + r.status);
        return r.json();
      })
      .then(function (j) {
        if (!j || !j.rates) throw new Error('primary shape');
        return j.rates;
      })
      .catch(function () {
        return fetch('https://open.er-api.com/v6/latest/USD')
          .then(function (r) {
            return r.json();
          })
          .then(function (j) {
            if (!j || j.result !== 'success' || !j.rates) throw new Error('fallback shape');
            return j.rates;
          });
      });
  }

  /** "$346.00 USD" / "From $121.00" -> 346 / 121 */
  function parseUsd(text) {
    var m = String(text).replace(/,/g, '').match(/\$\s*([0-9]+(?:\.[0-9]{1,2})?)/);
    return m ? parseFloat(m[1]) : NaN;
  }

  function format(amount, currency) {
    var symbol = SYMBOLS[currency] || '';
    var rounded = currency === 'INR' || currency === 'JPY' ? Math.round(amount) : Math.round(amount);
    try {
      return symbol + rounded.toLocaleString('en-US');
    } catch (e) {
      return symbol + rounded;
    }
  }

  function annotate(root, currency, rate) {
    var nodes = (root || document).querySelectorAll(
      '.price-item--regular, .price-item--sale'
    );

    Array.prototype.forEach.call(nodes, function (node) {
      // never annotate a struck-through compare-at price
      if (node.closest && node.closest('s')) {
        node.setAttribute(MARKED, 'skip');
        return;
      }
      var usd = parseUsd(node.textContent);
      var stamp = currency + ':' + rate + ':' + usd;
      var previous = node.nextElementSibling;
      var existing = previous && previous.classList.contains('kioraa-approx') ? previous : null;
      if (node.getAttribute(MARKED) === stamp && existing) return;
      node.setAttribute(MARKED, stamp);
      if (!isFinite(usd) || usd <= 0) {
        if (existing) existing.remove();
        return;
      }
      if (existing) {
        existing.querySelector('.kioraa-approx__amount').textContent = '≈ ' + format(usd * rate, currency) + ' ' + currency;
        return;
      }

      // Two spans so the amount can read clearly while the disclosure stays
      // present. Both matter: the amount is why a shopper stays, the note is
      // why the charge is never a surprise.
      var wrap = document.createElement('span');
      wrap.className = 'kioraa-approx';

      var amount = document.createElement('span');
      amount.className = 'kioraa-approx__amount';
      amount.textContent = '≈ ' + format(usd * rate, currency) + ' ' + currency;

      var note = document.createElement('span');
      note.className = 'kioraa-approx__note';
      note.textContent = 'charged in USD';

      wrap.appendChild(amount);
      wrap.appendChild(note);
      node.insertAdjacentElement('afterend', wrap);
    });
  }

  function start() {
    var currency = targetCurrency();
    if (!currency) return; // US or unknown — nothing to add

    var cached = readCache(currency);
    var ready = cached
      ? Promise.resolve(cached)
      : fetchRates().then(function (rates) {
          writeCache(rates);
          return rates[currency];
        });

    ready
      .then(function (rate) {
        if (typeof rate !== 'number' || !isFinite(rate)) return;

        annotate(document, currency, rate);

        // Some sections replace the block; Kinn updates its existing text node.
        var observer = new MutationObserver(function (mutations) {
          for (var i = 0; i < mutations.length; i++) {
            if (mutations[i].type === 'characterData' || (mutations[i].addedNodes && mutations[i].addedNodes.length)) {
              annotate(document, currency, rate);
              break;
            }
          }
        });
        observer.observe(document.body, { childList: true, characterData: true, subtree: true });
      })
      .catch(function () {
        /* rates unavailable — show USD only, which is the truthful default */
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
